import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
  Box, Typography, Select, MenuItem, Button,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, FormControl, InputLabel, CircularProgress,
  Tabs, Tab,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import toast from "react-hot-toast";
import { axiosConfig } from "../../axios/axiosConfig";
import ReactHotToaster from "../../components/common/ReactHotToaster";
import PaginationControls from "../../components/Pagination/PaginationControls";
import { REFERRAL_ROLE_LABELS, getLowerRoles } from "./referralRoles";

// logged-in referral role → upline list endpoint
const UPLINE_ENDPOINT_BY_ROLE = {
  ReferralAdmin: "broker",
  Broker: "bdm",
  BDM: "sub-broker",
};

// ── palette ───────────────────────────────────────────────────────────────
const pageBg = "#f7f8fa";
const cardBg = "#ffffff";
const cardBorder = "#e5e7eb";
const textMain = "#111827";
const textMuted = "#6b7280";
const accentGreen = "#0f9d6b";
const accentGreenBg = "#e6f6ef";
const negRed = "#c0392b";
const negRedBg = "#fdecea";

const fmtDate = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  return isNaN(d.getTime()) ? value : d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

const StatCard = ({ label, value }) => (
  <Box
    sx={{
      flex: 1,
      minWidth: 180,
      background: cardBg,
      border: `1px solid ${cardBorder}`,
      borderRadius: "12px",
      p: "18px 20px",
      display: "flex",
      flexDirection: "column",
      gap: 1,
    }}
  >
    <Typography sx={{ fontSize: 13, color: textMuted, fontWeight: 500 }}>{label}</Typography>
    <Typography sx={{ fontSize: 26, fontWeight: 700, color: textMain }}>{value}</Typography>
  </Box>
);

const ReferralDashboard = () => {
  const userState = useSelector((store) => store.auth);
  const currentReferralRole = userState?.user?.userDetails?.referral_role;

  // tabs / create options = every role below the logged-in user's referral_role
  const lowerRoles = useMemo(() => getLowerRoles(currentReferralRole), [currentReferralRole]);
  const visibleRoleTabs = useMemo(
    () => lowerRoles
      .filter((r) => r !== "EndUser")
      .map((r) => ({ value: r, label: REFERRAL_ROLE_LABELS[r] || r })),
    [lowerRoles]
  );

  const [activeRole, setActiveRole] = useState(() =>
    visibleRoleTabs.some((t) => t.value === "Broker") ? "Broker" : visibleRoleTabs[0]?.value || ""
  );
  const [statusFilter, setStatusFilter] = useState("All");
  const [pageSize, setPageSize] = useState(12);
  const [page, setPage] = useState(1);

  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);

  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({ phoneno: "", role: "", upline: "" });
  const [createLoading, setCreateLoading] = useState(false);
  const [uplineOptions, setUplineOptions] = useState([]);
  const [uplineLoading, setUplineLoading] = useState(false);

  const uplineEndpoint = UPLINE_ENDPOINT_BY_ROLE[currentReferralRole];
  const showUplineField = !!uplineEndpoint;

  const today = new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });

  const activeRoleLabel = REFERRAL_ROLE_LABELS[activeRole] || activeRole;

  const loadRows = async (role) => {
    setLoading(true);
    try {
      const res = await axiosConfig({
        method: "get",
        url: "/referral/hierarchy/role-wise-list",
        params: { role },
      });
      const data = res.data?.response;
      setRows(Array.isArray(data) ? data : []);
    } catch (error) {
      console.log("role-wise-list error: ", error);
      toast.error(error?.response?.data?.response || "Failed to load referral hierarchy");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!activeRole) {
      setRows([]);
      setLoading(false);
      return;
    }
    loadRows(activeRole);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeRole]);

  const filteredBrokers = useMemo(() => {
    if (statusFilter === "All") return rows;
    const wantActive = statusFilter === "Active";
    return rows.filter((b) => !!b.is_active === wantActive);
  }, [rows, statusFilter]);

  useEffect(() => {
    setPage(1);
  }, [statusFilter, pageSize, activeRole]);

  const paginatedBrokers = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredBrokers.slice(start, start + pageSize);
  }, [filteredBrokers, page, pageSize]);

  const stats = useMemo(() => ({
    total: rows.length,
    active: rows.filter((b) => b.is_active).length,
    pending: rows.filter((b) => !b.is_active).length,
  }), [rows]);

  const handleOpenCreate = () => {
    setCreateForm({ phoneno: "", role: "", upline: "" });
    setUplineOptions([]);
    setCreateOpen(true);
  };

  const handleCloseCreate = () => setCreateOpen(false);

  useEffect(() => {
    if (!createOpen || !showUplineField) {
      setUplineOptions([]);
      return;
    }
    const loadUplines = async () => {
      setUplineLoading(true);
      try {
        const res = await axiosConfig({
          method: "get",
          url: `/referral/hierarchy/upline/${uplineEndpoint}`,
        });
        const data = res.data?.response;
        setUplineOptions(Array.isArray(data) ? data : []);
      } catch (error) {
        console.log("upline list error: ", error);
        toast.error(error?.response?.data?.response || "Failed to load upline list");
      } finally {
        setUplineLoading(false);
      }
    };
    loadUplines();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createOpen, showUplineField, uplineEndpoint]);

  const handleSubmitCreate = async () => {
    if (!createForm.phoneno.trim() || !createForm.role || (showUplineField && !createForm.upline)) {
      toast.error("Please fill in phone number, role and upline");
      return;
    }
    setCreateLoading(true);
    try {
      const res = await axiosConfig({
        method: "post",
        url: "/referral/hierarchy/create-profile",
        data: {
          phoneno: createForm.phoneno.trim(),
          role: createForm.role,
          ...(showUplineField ? { upline_id: createForm.upline } : {}),
        },
      });
      const successMsg =
        typeof res?.data?.message === "string"
          ? res.data.message
          : typeof res?.data?.response === "string"
            ? res.data.response
            : "Profile created successfully";
      toast.success(successMsg);
      handleCloseCreate();
      loadRows(activeRole);
    } catch (error) {
      const errMsg = error?.response?.data?.message ?? error?.response?.data?.response;
      toast.error(typeof errMsg === "string" ? errMsg : "Failed to create profile");
    } finally {
      setCreateLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3, background: pageBg, minHeight: "100vh" }}>
      {/* Page header */}
      <Box sx={{ mb: 3 }}>
        <Typography sx={{ fontSize: 22, fontWeight: 700, color: textMain }}>Overview Dashboard</Typography>
        <Typography sx={{ fontSize: 13, color: textMuted, mt: "2px" }}>
          Complete overview of clients and business performance
        </Typography>
      </Box>

      {/* Role tabs */}
      <Box sx={{ mb: 3 }}>
        <Tabs
          value={activeRole || false}
          onChange={(e, value) => setActiveRole(value)}
          variant="scrollable"
          scrollButtons="auto"
          TabIndicatorProps={{ style: { background: accentGreen } }}
          sx={{
            minHeight: 44,
            "& .MuiTab-root": { textTransform: "none", fontWeight: 600, fontSize: 13.5, minHeight: 44, color: textMuted },
            "& .Mui-selected": { color: `${textMain} !important` },
          }}
        >
          {visibleRoleTabs.map((t) => (
            <Tab key={t.value} value={t.value} label={t.label} />
          ))}
        </Tabs>
      </Box>

      {/* Stat cards + status filter */}
      <Box sx={{ display: "flex", gap: 2, mb: 3, alignItems: "flex-start", flexWrap: "wrap" }}>
        <Box sx={{ display: "flex", gap: 2, flex: 1, flexWrap: "wrap" }}>
          <StatCard label={`Total ${activeRoleLabel}`} value={stats.total} />
          <StatCard label="Active" value={stats.active} />
          <StatCard label="Pending" value={stats.pending} />
        </Box>
        <Select
          size="small"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          sx={{ background: cardBg, borderRadius: "8px", minWidth: 110 }}
        >
          <MenuItem value="All">All</MenuItem>
          <MenuItem value="Active">Active</MenuItem>
          <MenuItem value="Pending">Pending</MenuItem>
        </Select>
      </Box>

      {/* Broker table */}
      <Box sx={{ background: cardBg, border: `1px solid ${cardBorder}`, borderRadius: "12px", overflow: "hidden" }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", p: "16px 20px", borderBottom: `1px solid ${cardBorder}` }}>
          <Typography sx={{ fontSize: 15, fontWeight: 700, color: textMain }}>{activeRoleLabel} (as of {today})</Typography>
          <Box sx={{ display: "flex", gap: 1.5 }}>
            <Button
              variant="outlined"
              onClick={() => loadRows(activeRole)}
              sx={{
                borderColor: cardBorder, color: textMain, borderRadius: "8px", textTransform: "none",
                fontWeight: 600, fontSize: 13, px: 2,
                "&:hover": { background: "#f3f4f6", borderColor: cardBorder },
              }}
            >
              Refresh
            </Button>
            {visibleRoleTabs.length > 0 && <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenCreate}
              sx={{
                background: "#111827", color: "#fff", borderRadius: "8px", textTransform: "none",
                fontWeight: 600, fontSize: 13, px: 2.5,
                "&:hover": { background: "#000" },
              }}
            >
              Create New
            </Button>}
          </Box>
        </Box>

        <Box sx={{ overflowX: "auto" }}>
          <Box component="table" sx={{ width: "100%", borderCollapse: "collapse" }}>
            <Box component="thead">
              <Box component="tr">
                {[`${activeRoleLabel} ID`, "Name", "Phone Number", "Email", "User ID", "Join Date", "Status", "Action"].map((h) => (
                  <Box
                    component="th"
                    key={h}
                    sx={{
                      textAlign: "left", fontSize: 11.5, color: textMuted, fontWeight: 600,
                      textTransform: "uppercase", letterSpacing: "0.3px",
                      p: "10px 20px", whiteSpace: "nowrap", borderBottom: `1px solid ${cardBorder}`,
                    }}
                  >
                    {h}
                  </Box>
                ))}
              </Box>
            </Box>
            <Box component="tbody">
              {loading ? (
                <Box component="tr">
                  <Box component="td" colSpan={8} sx={{ p: 5, textAlign: "center" }}>
                    <CircularProgress size={26} sx={{ color: accentGreen }} />
                  </Box>
                </Box>
              ) : paginatedBrokers.length === 0 ? (
                <Box component="tr">
                  <Box component="td" colSpan={8} sx={{ p: 5, textAlign: "center", fontSize: 13, color: textMuted }}>
                    No {activeRoleLabel} found
                  </Box>
                </Box>
              ) : (
                paginatedBrokers.map((b) => (
                  <Box component="tr" key={b.id}>
                    <Box component="td" sx={{ p: "12px 20px", fontSize: 13, color: textMain, borderBottom: `1px solid ${cardBorder}`, whiteSpace: "nowrap" }}>{b.id}</Box>
                    <Box component="td" sx={{ p: "12px 20px", fontSize: 13, color: textMain, borderBottom: `1px solid ${cardBorder}`, whiteSpace: "nowrap" }}>{b.user?.username || "—"}</Box>
                    <Box component="td" sx={{ p: "12px 20px", fontSize: 13, color: textMain, borderBottom: `1px solid ${cardBorder}`, whiteSpace: "nowrap" }}>{b.user?.phoneno || "—"}</Box>
                    <Box component="td" sx={{ p: "12px 20px", fontSize: 13, color: textMain, borderBottom: `1px solid ${cardBorder}`, whiteSpace: "nowrap" }}>{b.user?.email || "—"}</Box>
                    <Box component="td" sx={{ p: "12px 20px", fontSize: 13, color: textMain, borderBottom: `1px solid ${cardBorder}`, whiteSpace: "nowrap" }}>{b.user?.userid || "—"}</Box>
                    <Box component="td" sx={{ p: "12px 20px", fontSize: 13, color: textMain, borderBottom: `1px solid ${cardBorder}`, whiteSpace: "nowrap" }}>{fmtDate(b.created_at)}</Box>
                    <Box component="td" sx={{ p: "12px 20px", borderBottom: `1px solid ${cardBorder}`, whiteSpace: "nowrap" }}>
                      <Box
                        component="span"
                        sx={{
                          fontSize: 11.5, fontWeight: 700,
                          color: b.is_active ? accentGreen : negRed,
                          background: b.is_active ? accentGreenBg : negRedBg,
                          borderRadius: "999px", padding: "3px 10px",
                        }}
                      >
                        {b.is_active ? "Active" : "Inactive"}
                      </Box>
                    </Box>
                    <Box component="td" sx={{ p: "12px 20px", borderBottom: `1px solid ${cardBorder}`, whiteSpace: "nowrap" }}>
                      <Typography component="span" sx={{ fontSize: 13, fontWeight: 600, color: accentGreen, cursor: "pointer" }}>
                        View Detail
                      </Typography>
                    </Box>
                  </Box>
                ))
              )}
            </Box>
          </Box>
        </Box>

        {/* Footer: page size + pagination */}
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", p: "14px 20px" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography sx={{ fontSize: 13, color: textMuted }}>Show items</Typography>
            <Select
              size="small"
              value={pageSize}
              onChange={(e) => setPageSize(e.target.value)}
              sx={{ minWidth: 70 }}
            >
              {[12, 24, 48].map((n) => (
                <MenuItem key={n} value={n}>{n}</MenuItem>
              ))}
            </Select>
          </Box>
          <PaginationControls
            totalCount={filteredBrokers.length}
            page={page}
            setPage={setPage}
            offset={pageSize}
          />
        </Box>
      </Box>

      {/* Create New Dialog */}
      <Dialog open={createOpen} onClose={handleCloseCreate} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, fontSize: 16, color: textMain }}>Create New</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2.5, pt: 1 }}>
          <TextField
            label="Phone Number"
            fullWidth
            margin="dense"
            value={createForm.phoneno}
            onChange={(e) => setCreateForm((f) => ({ ...f, phoneno: e.target.value }))}
          />
          <FormControl fullWidth margin="dense">
            <InputLabel>Role</InputLabel>
            <Select
              label="Role"
              value={createForm.role}
              onChange={(e) => setCreateForm((f) => ({ ...f, role: e.target.value }))}
            >
              {visibleRoleTabs.map((t) => (
                <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {showUplineField && (
            <FormControl fullWidth margin="dense">
              <InputLabel>Upline</InputLabel>
              <Select
                label="Upline"
                value={createForm.upline}
                onChange={(e) => setCreateForm((f) => ({ ...f, upline: e.target.value }))}
              >
                {uplineOptions.length === 0 ? (
                  <MenuItem disabled value="">
                    {uplineLoading ? "Loading..." : "No upline available"}
                  </MenuItem>
                ) : (
                  uplineOptions.map((u) => (
                    <MenuItem key={u.id} value={u.id}>{u.username} ({u.role})</MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseCreate} sx={{ color: textMuted, fontWeight: 600 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmitCreate}
            disabled={createLoading || !createForm.phoneno.trim() || !createForm.role || (showUplineField && !createForm.upline)}
            sx={{
              background: "#111827", color: "#fff", borderRadius: "8px", textTransform: "none", fontWeight: 600, px: 3,
              "&:hover": { background: "#000" },
              "&:disabled": { background: "#9ca3af" },
            }}
          >
            {createLoading ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      <ReactHotToaster />
    </Box>
  );
};

export default ReferralDashboard;
